import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X, Search, FileText, ListChecks, PiggyBank, PoundSterling, Users, ClipboardList,
  CheckCircle2, Loader2, Upload, Plus, ChevronLeft, Sparkles,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const DEFAULT_REL = {
  document: "documented_by", budget_txn: "payment_for", budget: "budgeted_by",
  shopping_list: "shopping_for", contact: "contact_for", home_life_item: "prepares_for",
  reminder: "reminder_for", trip: "related_to", responsibility: "related_to",
};

export const TYPE_CHIPS = [
  { key: "document", label: "Documents", Icon: FileText, create: "upload" },
  { key: "shopping_list", label: "Lists", Icon: ListChecks, create: "list" },
  { key: "budget", label: "Budget", Icon: PiggyBank },
  { key: "budget_txn", label: "Payments", Icon: PoundSterling, create: "spend" },
  { key: "contact", label: "Contacts", Icon: Users },
  { key: "responsibility", label: "Hub records", Icon: ClipboardList },
  { key: "home_life_item", label: "Tasks", Icon: CheckCircle2, create: "task" },
];

const REL_CHOICES = [
  { key: "required", label: "Required before this event", make: (t) => ({ relationship_type: DEFAULT_REL[t] || "related_to", required_for_readiness: true }) },
  { key: "reference", label: "Reference", make: () => ({ relationship_type: "related_to" }) },
  { key: "warranty", label: "Warranty", make: () => ({ relationship_type: "documented_by", label: "Warranty" }) },
  { key: "invoice", label: "Invoice", make: () => ({ relationship_type: "documented_by", label: "Invoice" }) },
  { key: "receipt", label: "Receipt", make: () => ({ relationship_type: "documented_by", label: "Receipt" }) },
  { key: "supporting", label: "Supporting information", make: () => ({ relationship_type: "related_to", label: "Supporting information" }) },
  { key: "optional", label: "Optional", make: (t) => ({ relationship_type: DEFAULT_REL[t] || "related_to", required_for_readiness: false }) },
];

const chipIcon = (type) => (TYPE_CHIPS.find((c) => c.key === type) || {}).Icon || Sparkles;

// "How is this related?" step (mockup 4th mobile frame).
function RelateStep({ target, anchor, onBack, onDone }) {
  const [choice, setChoice] = useState("reference");
  const [busy, setBusy] = useState(false);
  const attach = () => {
    const opt = REL_CHOICES.find((c) => c.key === choice);
    setBusy(true);
    api.post("/record-links", {
      source_type: anchor.type, source_id: anchor.id,
      target_type: target.source_type, target_id: target.source_id,
      ...opt.make(target.source_type),
    })
      .then(() => { toast.success(`${target.title} attached`); onDone(); })
      .catch((e) => {
        if (e.response?.status === 409) { toast.info("Those records are already linked."); onDone(); }
        else toast.error(formatApiError(e.response?.data?.detail));
      })
      .finally(() => setBusy(false));
  };
  return (
    <div data-testid="ws-relate-step">
      <button data-testid="ws-relate-back" onClick={onBack} className="mb-3 flex items-center gap-1 text-xs font-medium text-[#636E72]">
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>
      <p className="text-sm font-semibold text-[#2D3436]">How is this related?</p>
      <p className="mt-0.5 truncate text-xs text-[#9aa3a0]">{target.title}</p>
      <div className="mt-3 space-y-1">
        {REL_CHOICES.map((c) => (
          <label key={c.key} data-testid={`ws-rel-${c.key}`}
            className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${choice === c.key ? "border-[#0F766E] bg-[#0F766E]/5 text-[#2D3436]" : "border-[#EBECE8] text-[#636E72]"}`}>
            <input type="radio" name="ws-rel" checked={choice === c.key} onChange={() => setChoice(c.key)} className="accent-[#0F766E]" />
            {c.label}
          </label>
        ))}
      </div>
      <button data-testid="ws-attach-commit" onClick={attach} disabled={busy}
        className="mt-4 w-full rounded-xl bg-[#0F766E] py-2.5 text-sm font-medium text-white disabled:opacity-50">
        {busy ? "Attaching…" : "Attach item"}
      </button>
    </div>
  );
}

// Quick create: new record made without leaving the workspace, then linked.
function CreateStep({ kind, anchor, onBack, onDone }) {
  const [form, setForm] = useState({ title: "", name: "", amount: "", due: "", budget_id: "" });
  const [budgets, setBudgets] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (kind === "spend") api.get("/budget/budgets").then((r) => {
      const bs = r.data.budgets || [];
      setBudgets(bs);
      if (bs[0]) setForm((f) => ({ ...f, budget_id: bs[0].id }));
    }).catch(() => {});
  }, [kind]);

  const link = (targetType, targetId, rel, extra = {}) =>
    api.post("/record-links", { source_type: anchor.type, source_id: anchor.id,
      target_type: targetType, target_id: targetId, relationship_type: rel,
      provenance_type: "created_from_event", ...extra }).catch(() => {});

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "task") {
        const r = await api.post("/home-life/items", { module_type: "chore", title: form.title, due_date: form.due || null });
        await link("home_life_item", r.data.item.id, "prepares_for");
        toast.success("Task created and attached");
      } else if (kind === "list") {
        const r = await api.post("/home-life/shopping-lists", { name: form.name });
        await link("shopping_list", (r.data.list || r.data).id, "shopping_for");
        toast.success("List created and attached");
      } else if (kind === "spend") {
        const r = await api.post(`/budget/budgets/${form.budget_id}/transactions`, {
          kind: "commitment", title: form.title, amount: parseFloat(form.amount), due_date: form.due || null });
        await link("budget_txn", (r.data.transaction || r.data).id, "payment_for");
        toast.success("Planned spend created and attached");
      } else if (kind === "upload") {
        const file = fileRef.current?.files?.[0];
        if (!file) { toast.error("Choose a file first."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "home");
        const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const docId = (r.data.document || r.data).id;
        await link("document", docId, "documented_by");
        toast.success("Document uploaded and attached");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-[#E2E8F0] px-3 py-2.5 text-sm focus:border-[#0F766E] focus:outline-none";
  const titles = { task: "New task", list: "New shopping list", spend: "New planned spend", upload: "Upload a document" };
  const valid = kind === "task" ? form.title.trim() : kind === "list" ? form.name.trim()
    : kind === "spend" ? (form.title.trim() && form.amount && form.budget_id) : true;
  return (
    <div data-testid="ws-create-step">
      <button data-testid="ws-create-back" onClick={onBack} className="mb-3 flex items-center gap-1 text-xs font-medium text-[#636E72]">
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </button>
      <p className="mb-3 text-sm font-semibold text-[#2D3436]">{titles[kind]}</p>
      <div className="space-y-2.5">
        {kind === "task" && (<>
          <input data-testid="ws-create-title" className={field} placeholder="What needs doing?" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input data-testid="ws-create-due" type="date" className={field} value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} />
        </>)}
        {kind === "list" && (
          <input data-testid="ws-create-name" className={field} placeholder="List name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        )}
        {kind === "spend" && (<>
          <select data-testid="ws-create-budget" className={field} value={form.budget_id} onChange={(e) => setForm({ ...form, budget_id: e.target.value })}>
            {budgets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input data-testid="ws-create-title" className={field} placeholder="What is this for?" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <input data-testid="ws-create-amount" type="number" min="0" step="0.01" className={field} placeholder="Amount (£)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input data-testid="ws-create-due" type="date" className={field} value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} />
          <p className="text-[10px] text-[#9aa3a0]">A planned amount, not actual spend. Record the real cost in Budget when paid.</p>
        </>)}
        {kind === "upload" && (
          <input data-testid="ws-create-file" ref={fileRef} type="file" className={field} />
        )}
      </div>
      <button data-testid="ws-create-submit" onClick={submit} disabled={!valid || busy}
        className="mt-4 w-full rounded-xl bg-[#0F766E] py-2.5 text-sm font-medium text-white disabled:opacity-50">
        {busy ? "Working…" : "Create and attach"}
      </button>
    </div>
  );
}

// Add-to-event sheet: global search first, optional type chips, attach or quick create.
// Desktop: centred small modal layered above the workspace. Mobile: bottom sheet.
export default function AttachSheet({ anchor, presetType, presetCreate, onClose, onDone, isMobile }) {
  const [type, setType] = useState(presetType || null);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(presetCreate ? { create: presetCreate } : null);

  useEffect(() => {
    if (step) return;
    setLoading(true);
    const t = setTimeout(() => {
      const req = type
        ? api.get(`/record-links/search-candidates?target_type=${type}&q=${encodeURIComponent(q)}`)
            .then((r) => (r.data.candidates || []).map((c) => ({ ...c, source_type: c.source_type || type })))
        : api.get(`/record-links/search-all?q=${encodeURIComponent(q)}`)
            .then((r) => r.data.results || []);
      req.then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [type, q, step]);

  const active = TYPE_CHIPS.find((c) => c.key === type);

  return (
    <div className={`fixed inset-0 z-[60] flex ${isMobile ? "items-end" : "items-center justify-center p-4"}`}
      data-testid="ws-attach-sheet" role="dialog" aria-modal="true" aria-label="Add to this event">
      <div className="absolute inset-0 bg-[#2D3436]/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full flex-col overflow-hidden bg-white shadow-2xl ${isMobile
        ? "max-h-[88vh] rounded-t-3xl" : "max-h-[82vh] max-w-lg rounded-3xl"}`}>
        {isMobile && <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[#E2E8F0]" />}
        <div className="flex items-center gap-3 px-5 pb-3 pt-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "rgba(15,118,110,0.08)" }}>
            <Plus className="h-4 w-4 text-[#0F766E]" />
          </span>
          <p className="flex-1 font-display text-base font-semibold text-[#2D3436]">Add to this event</p>
          <button data-testid="ws-attach-close" onClick={onClose} className="rounded-full bg-[#F4F6F2] p-1.5 text-[#636E72]"><X className="h-4 w-4" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
          {step?.relate ? (
            <RelateStep target={step.relate} anchor={anchor} onBack={() => setStep(null)} onDone={onDone} />
          ) : step?.create ? (
            <CreateStep kind={step.create} anchor={anchor} onBack={() => setStep(null)} onDone={onDone} />
          ) : (
            <>
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="ws-attach-tabs">
                {TYPE_CHIPS.map((c) => (
                  <button key={c.key} data-testid={`ws-tab-${c.key}`} onClick={() => setType(type === c.key ? null : c.key)}
                    className="flex shrink-0 flex-col items-center gap-1">
                    <span className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${type === c.key ? "bg-[#0F766E] text-white" : "bg-[#F4F6F2] text-[#636E72]"}`}>
                      <c.Icon style={{ height: 18, width: 18 }} />
                    </span>
                    <span className={`text-[10px] font-medium ${type === c.key ? "text-[#0F766E]" : "text-[#636E72]"}`}>{c.label}</span>
                  </button>
                ))}
              </div>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#9aa3a0]" />
                <input data-testid="ws-add-search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus={!isMobile}
                  placeholder={type ? `Search ${active.label.toLowerCase()}…` : "Search anything…"}
                  className="w-full rounded-xl border border-[#E2E8F0] py-2.5 pl-9 pr-3 text-sm focus:border-[#0F766E] focus:outline-none" />
              </div>
              {active?.create && (
                <button data-testid="ws-quick-create" onClick={() => setStep({ create: active.create })}
                  className="mt-2 flex w-full items-center gap-2 rounded-xl border border-dashed border-[#0F766E]/40 px-3.5 py-2.5 text-sm font-medium text-[#0F766E] hover:bg-[#0F766E]/5">
                  {active.create === "upload" ? <Upload className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {active.create === "upload" ? "Upload new document" : active.create === "task" ? "New task"
                    : active.create === "list" ? "New shopping list" : "New planned spend"}
                </button>
              )}
              <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">
                {q ? "Results" : "Suggested"}
              </p>
              <div className="mt-1.5 space-y-1.5" data-testid="ws-candidates">
                {loading ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-[#9aa3a0]" /></div>
                ) : rows.length === 0 ? (
                  <p className="py-6 text-center text-xs text-[#9aa3a0]">Nothing found. Try a different search or create something new.</p>
                ) : rows.map((c) => {
                  const Icon = chipIcon(c.source_type);
                  return (
                    <div key={`${c.source_type}-${c.source_id}`} data-testid={`ws-candidate-${c.source_id}`}
                      className="flex items-center gap-3 rounded-xl border border-[#EBECE8] bg-white px-3 py-2.5">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F6F2] text-[#0F766E]">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[#2D3436]">{c.title}</span>
                        <span className="block truncate text-[11px] text-[#9aa3a0]">
                          {[c.type_label, c.subtitle].filter(Boolean).join(" \u00b7 ")}
                        </span>
                      </span>
                      {c.badge && <span className="rounded bg-[#F4F6F2] px-1.5 py-0.5 text-[10px] font-semibold text-[#636E72]">{c.badge}</span>}
                      <button data-testid={`ws-attach-${c.source_id}`} onClick={() => setStep({ relate: c })}
                        className="rounded-lg border border-[#0F766E]/40 px-3 py-1.5 text-xs font-medium text-[#0F766E] hover:bg-[#0F766E]/5">
                        Attach
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
